import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin, assertLightAdmin, assertPageAction } from "@/lib/admin-guard.server";
import { cached, invalidateCached } from "@/lib/server-cache";
import {
  CONSENT_TEXT,
  SOCIAL_CONSENT_TEXT,
  IMAGE_BACKGROUNDS,
  type Testimonial,
  type TestimonialAdminRow,
} from "@/lib/testimonials";

const localeSchema = z.enum(["sv", "en", "es"]);
const tokenSchema = z.string().min(10).max(120);

type Row = Record<string, unknown>;

function mapPublic(row: Row): Testimonial {
  const prefs = (row["display_prefs"] ?? {}) as Record<string, unknown>;
  const showFirst = prefs["firstName"] !== false;
  const showLast = prefs["lastName"] !== false;
  const showRole = prefs["role"] !== false;
  return {
    id: String(row["id"]),
    rating: Number(row["rating"] ?? 5),
    quote: String(row["edited_quote"] || row["quote"] || ""),
    firstName: showFirst ? String(row["first_name"] ?? "") : "",
    lastName: showLast ? String(row["last_name"] ?? "") : "",
    roleLabel: showRole ? String(row["role_label"] ?? "") : "",
    imageUrl: String(row["image_url"] ?? ""),
    locale: String(row["locale"] ?? "sv"),
    isTest: Boolean(row["is_test"]),
    status: (row["status"] as Testimonial["status"]) ?? "draft",
    createdAt: String(row["created_at"] ?? ""),
    publishedAt: (row["published_at"] as string | null) ?? null,
    source: String(row["quote_source"] ?? ""),
    showOnHome: row["show_on_home"] !== false,
  };
}

function mapAdmin(row: Row): TestimonialAdminRow {
  const prefs = (row["display_prefs"] ?? {}) as Record<string, unknown>;
  return {
    ...mapPublic(row),
    // Admin must always receive the stored values. Public display preferences
    // are applied only in mapPublic and must never blank the edit form.
    firstName: String(row["first_name"] ?? ""),
    lastName: String(row["last_name"] ?? ""),
    roleLabel: String(row["role_label"] ?? ""),
    email: String(row["email"] ?? ""),
    quoteSource: String(row["quote_source"] ?? "own"),
    aiPrompt: String(row["ai_prompt"] ?? ""),
    aiDraft: String(row["ai_draft"] ?? ""),
    editedQuote: String(row["edited_quote"] ?? ""),
    originalImageUrl: String(row["original_image_url"] ?? ""),
    imageChoice: String(row["image_choice"] ?? "none"),
    imageBackground: String(row["image_background"] ?? ""),
    imageVariants: Array.isArray(row["image_variants"]) ? (row["image_variants"] as string[]) : [],
    consentGiven: Boolean(row["consent_given"]),
    consentAt: (row["consent_at"] as string | null) ?? null,
    consentText: String(row["consent_text"] ?? ""),
    adminNote: String(row["admin_note"] ?? ""),
    sourceChannel: String(row["source_channel"] ?? ""),
    sortOrder: Number(row["sort_order"] ?? 0),
    publishedAt: (row["published_at"] as string | null) ?? null,
    inviteId: (row["invite_id"] as string | null) ?? null,
    translationGroupId: String(row["translation_group_id"] ?? row["id"] ?? ""),
    updatedAt: String(row["updated_at"] ?? ""),
    improvementFeedback: String(row["improvement_feedback"] ?? ""),
    socialConsent: Boolean(row["social_consent"]),
    socialConsentText: String(row["social_consent_text"] ?? ""),
    displayPrefs: {
      firstName: prefs["firstName"] !== false,
      lastName: prefs["lastName"] !== false,
      role: prefs["role"] !== false,
    },
  };
}

/* ------------------------------------------------------------------ publikt */

/** Publicerade omdömen till startsidan. Filtreras på språk när locale anges. */
export const listPublishedTestimonials = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z
      .object({
        limit: z.number().int().min(1).max(24).default(6),
        locale: z.enum(["sv", "en", "es"]).optional(),
        homeOnly: z.boolean().optional(),
      })
      .parse(data ?? {}),
  )
  .handler(async ({ data }) =>
    cached(
      `testimonials:${data.locale ?? "all"}:${data.limit}:${data.homeOnly ? "home" : "all"}`,
      60_000,
      async () => {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        let query = supabaseAdmin
          .from("testimonials")
          // Explicit kolumnlista – publika ytor läser aldrig hela raden.
          .select(
            "id, rating, quote, edited_quote, first_name, last_name, role_label, image_url, locale, is_test, status, created_at, published_at, quote_source, show_on_home, display_prefs",
          )

          .eq("status", "published")
          .is("deleted_at", null)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: false })
          .limit(data.limit);
        if (data.locale) query = query.eq("locale", data.locale);
        if (data.homeOnly) query = query.eq("show_on_home", true);
        const { data: rows } = await query;
        return (rows ?? []).map((r) => mapPublic(r as Row));
      },
    ),
  );

async function inviteByToken(token: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: invite } = await supabaseAdmin
    .from("testimonial_invites")
    .select("*")
    .eq("token", token)
    .maybeSingle();
  if (!invite) return { invite: null as null, reason: "missing" as const };
  if (invite.revoked_at) return { invite: null, reason: "revoked" as const };
  if (new Date(invite.expires_at).getTime() < Date.now())
    return { invite: null, reason: "expired" as const };
  return { invite, reason: null };
}

/** Öppnar det personliga formuläret och hämtar eventuellt påbörjat svar. */
export const openTestimonialInvite = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => z.object({ token: tokenSchema }).parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    const { DEFAULT_STATEMENTS } = await import("@/lib/testimonials");
    const { invite, reason } = await inviteByToken(data.token);
    if (!invite) return { ok: false as const, reason };

    const existingQuery = supabaseAdmin
      .from("testimonials")
      .select("*")
      .eq("invite_id", invite.id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    const settingsQuery = supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", "testimonial_invite_copy")
      .maybeSingle();
    const openedWrite =
      invite.status === "sent"
        ? Promise.all([
            supabaseAdmin
              .from("testimonial_invites")
              .update({ status: "opened", opened_at: new Date().toISOString() })
              .eq("id", invite.id),
            logTestimonialEvent({ inviteId: invite.id, event: "opened" }),
          ])
        : Promise.resolve();
    const [{ data: existing }, { data: settings }] = await Promise.all([
      existingQuery,
      settingsQuery,
      openedWrite,
    ]);
    const locale = invite.locale as "sv" | "en" | "es";
    const allCopy = (settings?.value ?? {}) as Record<string, { statements?: unknown }>;
    const savedStatements = allCopy[locale]?.statements;
    const statements =
      Array.isArray(savedStatements) && savedStatements.length > 0
        ? savedStatements.filter(
            (item): item is string => typeof item === "string" && item.trim().length > 0,
          )
        : DEFAULT_STATEMENTS[locale];

    return {
      ok: true as const,
      invite: {
        id: invite.id,
        email: invite.email,
        name: invite.name,
        locale: invite.locale,
        isTest: invite.is_test,
        submitted: invite.status === "submitted",
      },
      draft: existing ? mapAdmin(existing as Row) : null,
      statements,
    };
  });

const draftSchema = z.object({
  token: tokenSchema,
  rating: z.number().int().min(1).max(5),
  quote: z.string().max(1000).default(""),
  improvementFeedback: z.string().max(1000).default(""),
  quoteSource: z.enum(["own", "preset", "ai"]).default("own"),
  aiPrompt: z.string().max(1000).default(""),
  aiDraft: z.string().max(1000).default(""),
  firstName: z.string().max(60).default(""),
  lastName: z.string().max(60).default(""),
  roleLabel: z.string().max(80).default(""),
  originalImageUrl: z.string().max(400).default(""),
  imageUrl: z.string().max(400).default(""),
  imageChoice: z.enum(["none", "original", "ai"]).default("none"),
  imageBackground: z.string().max(20).default(""),
  imageVariants: z.array(z.string().max(400)).max(6).default([]),
});

/** Sparar det föräldern hunnit fylla i (autospar). */
export const saveTestimonialDraft = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => draftSchema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    const { invite } = await inviteByToken(data.token);
    if (!invite) return { ok: false as const };

    const payload = {
      invite_id: invite.id,
      email: invite.email,
      locale: invite.locale,
      rating: data.rating,
      quote: data.quote,
      improvement_feedback: data.rating <= 4 ? data.improvementFeedback : "",
      quote_source: data.quoteSource,
      ai_prompt: data.aiPrompt,
      ai_draft: data.aiDraft,
      first_name: data.firstName,
      last_name: data.lastName,
      role_label: data.roleLabel,
      original_image_url: data.originalImageUrl,
      image_url: data.imageUrl,
      image_choice: data.imageChoice,
      image_background: data.imageBackground,
      image_variants: data.imageVariants as never,
      is_test: invite.is_test,
    };

    const { data: existing } = await supabaseAdmin
      .from("testimonials")
      .select("id,status")
      .eq("invite_id", invite.id)
      .limit(1)
      .maybeSingle();

    if (existing) {
      if (existing.status !== "draft") return { ok: true as const, id: existing.id };
      await supabaseAdmin.from("testimonials").update(payload).eq("id", existing.id);
      return { ok: true as const, id: existing.id };
    }

    const { data: created } = await supabaseAdmin
      .from("testimonials")
      .insert({ ...payload, status: "draft" })
      .select("id")
      .single();

    if (invite.status === "opened" || invite.status === "sent") {
      await supabaseAdmin
        .from("testimonial_invites")
        .update({ status: "started" })
        .eq("id", invite.id);
    }
    await logTestimonialEvent({
      inviteId: invite.id,
      testimonialId: created?.id ?? null,
      event: "started",
    });
    return { ok: true as const, id: created?.id ?? "" };
  });

/** AI formulerar ett citat utifrån förälderns stödord. */
export const suggestTestimonialQuote = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        token: tokenSchema,
        notes: z.string().min(3).max(800),
        rating: z.number().int().min(1).max(5),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { invite } = await inviteByToken(data.token);
    if (!invite) throw new Error("Länken är inte giltig längre.");
    const { draftQuote, logTestimonialEvent } = await import("@/lib/testimonials.server");
    const quote = await draftQuote(data.notes, invite.locale as "sv" | "en" | "es", data.rating);
    await logTestimonialEvent({
      inviteId: invite.id,
      event: "ai_quote",
      detail: quote,
      meta: { notes: data.notes },
    });
    return { quote };
  });

/** Påståendena som visas i formuläret, på inbjudans språk. */
export const getTestimonialFormOptions = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => z.object({ token: tokenSchema }).parse(data))
  .handler(async ({ data }): Promise<{ statements: string[] }> => {
    const { invite } = await inviteByToken(data.token);
    const locale = (invite?.locale ?? "sv") as "sv" | "en" | "es";
    const { DEFAULT_STATEMENTS } = await import("@/lib/testimonials");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", "testimonial_invite_copy")
      .maybeSingle();
    const all = (row?.value ?? {}) as Record<string, { statements?: unknown }>;
    const saved = all[locale]?.statements;
    const statements =
      Array.isArray(saved) && saved.length > 0
        ? saved.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
        : DEFAULT_STATEMENTS[locale];
    return { statements };
  });

/** AI formulerar tre olika förslag utifrån påståenden och egna ord. */
export const suggestTestimonialQuotes = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        token: tokenSchema,
        statements: z.array(z.string().trim().max(200)).max(40).default([]),
        notes: z.string().max(800).default(""),
        rating: z.number().int().min(1).max(5),
      })
      .parse(data),
  )
  .handler(async ({ data }): Promise<{ options: string[] }> => {
    const { invite } = await inviteByToken(data.token);
    if (!invite) throw new Error("Länken är inte giltig längre.");
    if (data.statements.length === 0 && data.notes.trim().length < 3) {
      throw new Error("Kryssa i något eller skriv några ord först.");
    }
    const { draftQuoteOptions, logTestimonialEvent } = await import("@/lib/testimonials.server");
    const locale = invite.locale as "sv" | "en" | "es";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: existingRows } = await supabaseAdmin
      .from("testimonials")
      .select("quote")
      .eq("status", "published")
      .eq("locale", locale)
      .is("deleted_at", null)
      .order("created_at", { ascending: false })
      .limit(25);
    const existingQuotes = (existingRows ?? [])
      .map((row) => (row as { quote?: string | null }).quote ?? "")
      .filter((quote) => quote.trim().length > 0);
    const { options, discarded, comparedWith } = await draftQuoteOptions({
      statements: data.statements,
      notes: data.notes,
      locale,
      rating: data.rating,
      existingQuotes,
      name: invite.name,
    });
    await logTestimonialEvent({
      inviteId: invite.id,
      event: "ai_quote_options",
      detail: options[0] ?? "",
      meta: { statements: data.statements, notes: data.notes, options, discarded, comparedWith },
    });
    return { options };
  });

/** Laddar upp förälderns egen bild. */
export const uploadTestimonialPhoto = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z.object({ token: tokenSchema, dataUrl: z.string().min(40).max(12_000_000) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { invite } = await inviteByToken(data.token);
    if (!invite) throw new Error("Länken är inte giltig längre.");
    const { storeTestimonialImage, logTestimonialEvent } =
      await import("@/lib/testimonials.server");
    const url = await storeTestimonialImage(data.dataUrl, `original/${invite.id}`);
    await logTestimonialEvent({ inviteId: invite.id, event: "photo_uploaded", detail: url });
    return { url };
  });

/** AI byter bakgrund på porträttet. */
export const restyleTestimonialPhoto = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        token: tokenSchema,
        imageUrl: z.string().min(5).max(400),
        background: z.enum(IMAGE_BACKGROUNDS),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { invite } = await inviteByToken(data.token);
    if (!invite) throw new Error("Länken är inte giltig längre.");
    const { readTestimonialImage, restylePortrait, storeTestimonialImage, logTestimonialEvent } =
      await import("@/lib/testimonials.server");
    const source = await readTestimonialImage(data.imageUrl);
    const generated = await restylePortrait(source, data.background);
    const url = await storeTestimonialImage(generated, `ai/${invite.id}`);
    await logTestimonialEvent({
      inviteId: invite.id,
      event: "photo_restyled",
      detail: url,
      meta: { background: data.background },
    });
    return { url };
  });

/** Skickar in omdömet för granskning. */
export const submitTestimonial = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    draftSchema
      .extend({
        firstName: z.string().min(1, "Skriv ditt förnamn.").max(60),
        quote: z.string().min(20, "Skriv några meningar.").max(1000),
        consent: z.literal(true),
        socialConsent: z.boolean().default(false),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    const { invite, reason } = await inviteByToken(data.token);
    if (!invite) return { ok: false as const, reason };

    const locale = (invite.locale as "sv" | "en" | "es") ?? "sv";
    const now = new Date().toISOString();
    const payload = {
      invite_id: invite.id,
      email: invite.email,
      locale,
      rating: data.rating,
      quote: data.quote,
      improvement_feedback: data.rating <= 4 ? data.improvementFeedback : "",
      quote_source: data.quoteSource,
      ai_prompt: data.aiPrompt,
      ai_draft: data.aiDraft,
      first_name: data.firstName,
      last_name: data.lastName,
      role_label: data.roleLabel,
      original_image_url: data.originalImageUrl,
      image_url: data.imageUrl,
      image_choice: data.imageChoice,
      image_background: data.imageBackground,
      image_variants: data.imageVariants as never,
      consent_given: true,
      consent_at: now,
      consent_text: CONSENT_TEXT[locale],
      social_consent: data.socialConsent,
      social_consent_text: data.socialConsent ? SOCIAL_CONSENT_TEXT[locale] : "",
      status: "submitted",
      is_test: invite.is_test,
    };

    const { data: existing } = await supabaseAdmin
      .from("testimonials")
      .select("id")
      .eq("invite_id", invite.id)
      .limit(1)
      .maybeSingle();

    let id = existing?.id ?? "";
    if (existing) {
      await supabaseAdmin.from("testimonials").update(payload).eq("id", existing.id);
    } else {
      const { data: created } = await supabaseAdmin
        .from("testimonials")
        .insert(payload)
        .select("id")
        .single();
      id = created?.id ?? "";
    }

    await supabaseAdmin
      .from("testimonial_invites")
      .update({ status: "submitted", submitted_at: now })
      .eq("id", invite.id);
    await logTestimonialEvent({ inviteId: invite.id, testimonialId: id, event: "submitted" });
    if (data.rating <= 4 && data.improvementFeedback.trim().length > 0) {
      await logTestimonialEvent({
        inviteId: invite.id,
        testimonialId: id,
        event: "improvement_feedback_given",
        detail: data.improvementFeedback.trim().slice(0, 500),
        meta: { rating: data.rating },
      });
    }
    await (
      await import("@/lib/push.server")
    ).notify("testimonial", {
      title: "Nytt omdöme inskickat",
      body: "Ett nytt omdöme väntar på granskning.",
      url: "/admin/omdomen",
    });
    await logTestimonialEvent({
      inviteId: invite.id,
      testimonialId: id,
      event: "consent",
      detail: CONSENT_TEXT[locale],
    });

    invalidateCached("testimonials");
    return { ok: true as const, id };
  });

/* -------------------------------------------------------------------- admin */

/** Alla omdömen, inbjudningar och nyckeltal för adminvyn. */
export const listTestimonialsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertLightAdmin(context as never, "/admin/omdomen");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: rows }, { data: invites }, { data: events }] = await Promise.all([
      supabaseAdmin
        .from("testimonials")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(500),
      supabaseAdmin
        .from("testimonial_invites")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500),
      supabaseAdmin
        .from("testimonial_events")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(400),
    ]);

    // Leveransstatus per mottagare hämtas ur mejlloggen (senaste posten vinner).
    const recipients = [...new Set((invites ?? []).map((i) => i.email))];
    const delivery = new Map<string, { status: string; at: string; error: string }>();
    if (recipients.length > 0) {
      const { data: mails } = await supabaseAdmin
        .from("email_log")
        .select("recipient, status, error, created_at")
        .in("recipient", recipients)
        .order("created_at", { ascending: false })
        .limit(1000);
      for (const mail of mails ?? []) {
        const key = String(mail.recipient ?? "").toLowerCase();
        if (!key || delivery.has(key)) continue;
        delivery.set(key, {
          status: String(mail.status ?? ""),
          at: String(mail.created_at ?? ""),
          error: String(mail.error ?? ""),
        });
      }
    }

    const list = (rows ?? []).map((r) => mapAdmin(r as Row));
    const inviteList = (invites ?? []).map((i) => {
      const mail = delivery.get(String(i.email ?? "").toLowerCase()) ?? null;
      return {
        id: i.id,
        email: i.email,
        name: i.name,
        locale: i.locale,
        status: i.status,
        token: i.token,
        isTest: i.is_test,
        createdAt: i.created_at,
        sentAt: (i as { sent_at?: string | null }).sent_at ?? i.created_at,
        createdByEmail: (i as { created_by_email?: string }).created_by_email ?? "",
        message: (i as { message?: string }).message ?? "",
        openedAt: i.opened_at,
        submittedAt: i.submitted_at,
        expiresAt: i.expires_at,
        revokedAt: i.revoked_at,
        reminderCount: i.reminder_count ?? 0,
        reminderAt: (i as { reminder_at?: string | null }).reminder_at ?? null,
        channel: (i as { channel?: string }).channel ?? "email",
        note: (i as { note?: string }).note ?? "",
        mailStatus: mail?.status ?? "",
        mailAt: mail?.at ?? "",
        mailError: mail?.error ?? "",
      };
    });

    const stats = {
      invites: inviteList.length,
      opened: inviteList.filter((i) => i.openedAt).length,
      submitted: inviteList.filter((i) => i.submittedAt).length,
      published: new Set(
        list.filter((t) => t.status === "published").map((t) => t.translationGroupId || t.id),
      ).size,
      aiQuotes: list.filter((t) => t.quoteSource === "ai").length,
      aiImages: list.filter((t) => t.imageChoice === "ai").length,
      averageRating:
        list.length > 0
          ? Math.round((list.reduce((s, t) => s + t.rating, 0) / list.length) * 10) / 10
          : 0,
    };

    return {
      testimonials: list,
      invites: inviteList,
      events: (events ?? []).map((e) => ({
        id: e.id,
        testimonialId: e.testimonial_id,
        inviteId: e.invite_id,
        event: e.event,
        detail: e.detail,
        actorEmail: e.actor_email,
        createdAt: e.created_at,
      })),
      stats,
    };
  });

/** Kollar om mejladressen redan fått en inbjudan eller lämnat ett omdöme. */
export const checkTestimonialEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ email: z.string().trim().max(200) }).parse(data))
  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/omdomen");
    const email = data.email.trim().toLowerCase();
    if (!email.includes("@")) return { invites: [], testimonials: [] };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: invites }, { data: rows }] = await Promise.all([
      supabaseAdmin
        .from("testimonial_invites")
        .select("id, email, name, status, created_at, opened_at, submitted_at, revoked_at")
        .eq("email", email)
        .order("created_at", { ascending: false })
        .limit(10),
      supabaseAdmin
        .from("testimonials")
        .select("id, status, created_at")
        .eq("email", email)
        .is("deleted_at", null)
        .limit(10),
    ]);
    return {
      invites: (invites ?? []).map((i) => ({
        id: i.id,
        name: i.name ?? "",
        status: i.status ?? "sent",
        createdAt: i.created_at,
        sentAt: (i as { sent_at?: string | null }).sent_at ?? i.created_at,
        createdByEmail: (i as { created_by_email?: string }).created_by_email ?? "",
        message: (i as { message?: string }).message ?? "",
        openedAt: i.opened_at,
        submittedAt: i.submitted_at,
        revokedAt: i.revoked_at,
      })),
      testimonials: (rows ?? []).map((t) => ({
        id: t.id,
        status: t.status ?? "draft",
        createdAt: t.created_at,
      })),
    };
  });

const overridesSchema = z
  .object({
    subject: z.string().trim().max(200).optional(),
    reminderPrefix: z.string().trim().max(60).optional(),
    heading: z.string().trim().max(200).optional(),
    intro: z.string().trim().max(1200).optional(),
    button: z.string().trim().max(80).optional(),
    footnote: z.string().trim().max(1200).optional(),
  })
  .default({});

/** Standardtexterna för mejlet, per språk. Källan är inställningen i databasen. */
export const getTestimonialInviteCopy = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ locale: localeSchema.default("sv") }).parse(data))
  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/omdomen");
    const { inviteCopy } = await import("@/lib/testimonials.server");
    return await inviteCopy(data.locale);
  });

/** Sparar standardtexterna permanent för ett språk. */
export const saveTestimonialInviteCopy = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ locale: localeSchema, copy: overridesSchema }).parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never, "/admin/omdomen");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", "testimonial_invite_copy")
      .maybeSingle();
    const all = (row?.value ?? {}) as Record<string, Record<string, string>>;
    const current = all[data.locale] ?? {};
    const patch = Object.fromEntries(
      Object.entries(data.copy).filter(([, v]) => typeof v === "string" && v.trim().length > 0),
    );
    const next = { ...all, [data.locale]: { ...current, ...patch } };
    const { error } = await supabaseAdmin
      .from("app_settings")
      .upsert({ key: "testimonial_invite_copy", value: next as never }, { onConflict: "key" });
    if (error) throw new Error("Texterna kunde inte sparas.");
    return { ok: true as const };
  });

/** Renderar mejlet exakt som det skickas, för förhandsvisning i admin. */
export const previewTestimonialInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        name: z.string().trim().max(80).default(""),
        locale: localeSchema.default("sv"),
        message: z.string().trim().max(600).default(""),
        reminder: z.boolean().default(false),
        overrides: overridesSchema,
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/omdomen");
    const { buildInviteEmail } = await import("@/lib/testimonials.server");
    const mail = await buildInviteEmail({
      name: data.name,
      locale: data.locale,
      token: "forhandsvisning",
      message: data.message,
      reminder: data.reminder,
      overrides: data.overrides,
      preview: true,
    });
    return { subject: mail.subject, html: mail.html, url: mail.url };
  });

/** Skapar och mejlar en personlig inbjudan. */
export const createTestimonialInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        email: z.string().trim().email("Ogiltig mejladress.").max(200),
        name: z.string().trim().max(80).default(""),
        locale: localeSchema.default("sv"),
        message: z.string().trim().max(600).default(""),
        isTest: z.boolean().default(false),
        send: z.boolean().default(true),
        overrides: overridesSchema,
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never, "/admin/omdomen");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { sendInviteEmail, logTestimonialEvent } = await import("@/lib/testimonials.server");

    const token = crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().slice(0, 8);
    const { data: invite, error } = await supabaseAdmin
      .from("testimonial_invites")
      .insert({
        token,
        email: data.email.toLowerCase(),
        name: data.name,
        locale: data.locale,
        message: data.message,
        is_test: data.isTest,
        created_by_email: actorEmail,
        sent_at: data.send ? new Date().toISOString() : null,
      })
      .select("*")
      .single();
    if (error || !invite) throw new Error("Inbjudan kunde inte skapas.");

    let mail: { sent: boolean; error?: string; url: string; subject?: string } = {
      sent: false,
      url: "",
    };
    if (data.send) {
      mail = await sendInviteEmail({
        to: data.email,
        name: data.name,
        locale: data.locale,
        token,
        message: data.message,
        reminder: false,
        overrides: data.overrides,
      });
    }

    await logTestimonialEvent({
      inviteId: invite.id,
      event: data.send ? "invite_sent" : "invite_created",
      detail: data.email,
      actorEmail: actorEmail,
      meta: { subject: mail.subject ?? "", overrides: data.overrides },
    });

    return { id: invite.id, token, sent: mail.sent, error: mail.error ?? "", url: mail.url };
  });

/** Skickar en påminnelse på en befintlig inbjudan. */
export const remindTestimonialInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never, "/admin/omdomen");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { sendInviteEmail, logTestimonialEvent } = await import("@/lib/testimonials.server");
    const { data: invite } = await supabaseAdmin
      .from("testimonial_invites")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (!invite) throw new Error("Inbjudan hittades inte.");

    const mail = await sendInviteEmail({
      to: invite.email,
      name: invite.name,
      locale: invite.locale as "sv" | "en" | "es",
      token: invite.token,
      message: invite.message,
      reminder: true,
    });
    await supabaseAdmin
      .from("testimonial_invites")
      .update({ reminder_count: invite.reminder_count + 1, reminder_at: new Date().toISOString() })
      .eq("id", invite.id);
    await logTestimonialEvent({
      inviteId: invite.id,
      event: "reminder_sent",
      actorEmail: actorEmail,
    });
    return { sent: mail.sent, error: mail.error ?? "" };
  });

/** Återkallar en inbjudan så länken slutar fungera. */
export const revokeTestimonialInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never, "/admin/omdomen");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    await supabaseAdmin
      .from("testimonial_invites")
      .update({ status: "revoked", revoked_at: new Date().toISOString() })
      .eq("id", data.id);
    await logTestimonialEvent({
      inviteId: data.id,
      event: "invite_revoked",
      actorEmail: actorEmail,
    });
    invalidateCached("testimonials");
    return { ok: true };
  });

/** Tar bort en inbjudan. Ett påbörjat omdöme följer med till papperskorgen. */
export const deleteTestimonialInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertPageAction(context as never, "/admin/omdomen", "delete");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    const { softDeleteRow } = await import("@/lib/trash.server");

    const { data: linked } = await supabaseAdmin
      .from("testimonials")
      .select("id")
      .eq("invite_id", data.id)
      .is("deleted_at", null);

    for (const row of linked ?? []) {
      await softDeleteRow(context as never, "testimonials", row.id);
    }

    await logTestimonialEvent({
      inviteId: data.id,
      event: "invite_deleted",
      detail: linked?.length ? `Flyttade ${linked.length} omdöme till papperskorgen.` : "",
      actorEmail,
    });

    const { error } = await supabaseAdmin.from("testimonial_invites").delete().eq("id", data.id);
    if (error) throw new Error("Inbjudan kunde inte tas bort.");
    invalidateCached("testimonials");
    return { ok: true as const, movedToTrash: linked?.length ?? 0 };
  });

/** Uppdaterar, godkänner eller publicerar ett omdöme. */
export const updateTestimonial = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["draft", "submitted", "approved", "published", "rejected"]).optional(),
        editedQuote: z.string().max(1000).optional(),
        roleLabel: z.string().max(80).optional(),
        firstName: z.string().max(60).optional(),
        lastName: z.string().max(60).optional(),
        adminNote: z.string().max(600).optional(),
        sourceChannel: z.string().trim().max(120).optional(),
        sortOrder: z.number().int().min(0).max(999).optional(),
        showOnHome: z.boolean().optional(),
        imageUrl: z.string().max(400).optional(),
        rating: z.number().int().min(1).max(5).optional(),
        email: z.string().trim().email().max(160).optional(),
        locale: z.enum(["sv", "en", "es"]).optional(),
        quote: z.string().max(1000).optional(),
        quoteSource: z.enum(["own", "preset", "ai", "manual", "instagram"]).optional(),
        aiPrompt: z.string().max(1000).optional(),
        aiDraft: z.string().max(1000).optional(),
        improvementFeedback: z.string().max(1000).optional(),
        imageChoice: z.enum(["none", "original", "ai"]).optional(),
        imageBackground: z.string().max(40).optional(),
        imageVariants: z.array(z.string().max(400)).max(12).optional(),
        consentGiven: z.boolean().optional(),
        socialConsent: z.boolean().optional(),
        displayPrefs: z
          .object({ firstName: z.boolean(), lastName: z.boolean(), role: z.boolean() })
          .optional(),
        isTest: z.boolean().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/omdomen");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    await (
      await import("@/lib/revisions.server")
    ).saveRevision(context as never, "testimonials", data.id, "Före ändring i admin");

    const patch: Record<string, string | number | null> = {
      reviewed_by_email: actorEmail,
    };
    if (data.status) {
      patch["status"] = data.status;
      patch["published_at"] = data.status === "published" ? new Date().toISOString() : null;
    }
    if (data.editedQuote !== undefined) patch["edited_quote"] = data.editedQuote;
    if (data.roleLabel !== undefined) patch["role_label"] = data.roleLabel;
    if (data.firstName !== undefined) patch["first_name"] = data.firstName;
    if (data.lastName !== undefined) patch["last_name"] = data.lastName;
    if (data.adminNote !== undefined) patch["admin_note"] = data.adminNote;
    if (data.sourceChannel !== undefined) patch["source_channel"] = data.sourceChannel;
    if (data.sortOrder !== undefined) patch["sort_order"] = data.sortOrder;
    if (data.showOnHome !== undefined) patch["show_on_home"] = data.showOnHome as never;
    if (data.imageUrl !== undefined) patch["image_url"] = data.imageUrl;
    if (data.rating !== undefined) patch["rating"] = data.rating;
    if (data.email !== undefined) patch["email"] = data.email.toLowerCase();
    if (data.locale !== undefined) patch["locale"] = data.locale;
    if (data.quote !== undefined) patch["quote"] = data.quote;
    if (data.quoteSource !== undefined) patch["quote_source"] = data.quoteSource;
    if (data.aiPrompt !== undefined) patch["ai_prompt"] = data.aiPrompt;
    if (data.aiDraft !== undefined) patch["ai_draft"] = data.aiDraft;
    if (data.improvementFeedback !== undefined)
      patch["improvement_feedback"] = data.improvementFeedback;
    if (data.imageChoice !== undefined) patch["image_choice"] = data.imageChoice;
    if (data.imageBackground !== undefined) patch["image_background"] = data.imageBackground;
    if (data.imageVariants !== undefined) patch["image_variants"] = data.imageVariants as never;
    if (data.consentGiven !== undefined) patch["consent_given"] = data.consentGiven as never;
    if (data.socialConsent !== undefined) patch["social_consent"] = data.socialConsent as never;
    if (data.displayPrefs !== undefined) patch["display_prefs"] = data.displayPrefs as never;
    if (data.isTest !== undefined) patch["is_test"] = data.isTest as never;

    const { error } = await supabaseAdmin
      .from("testimonials")
      .update(patch as never)
      .eq("id", data.id);
    if (error) throw new Error("Omdömet kunde inte uppdateras.");

    /*
     * Ett omdöme är samma person oavsett språk. Status och personuppgifter
     * gäller därför hela språkfamiljen – man ska aldrig behöva godkänna
     * engelska och spanska separat. Texten (citat/roll) är per språk.
     */
    const sharedKeys = [
      "status",
      "published_at",
      "first_name",
      "last_name",
      "email",
      "rating",
      "image_url",
      "is_test",
      "show_on_home",
      "sort_order",
      "display_prefs",
      "consent_given",
      "social_consent",
    ];
    const shared: Record<string, unknown> = {};
    for (const key of sharedKeys) if (key in patch) shared[key] = patch[key];
    if (Object.keys(shared).length > 0) {
      const { data: self } = await supabaseAdmin
        .from("testimonials")
        .select("translation_group_id")
        .eq("id", data.id)
        .maybeSingle();
      const groupId = (self as { translation_group_id?: string } | null)?.translation_group_id;
      if (groupId) {
        await supabaseAdmin
          .from("testimonials")
          .update(shared as never)
          .eq("translation_group_id", groupId)
          .neq("id", data.id)
          .is("deleted_at", null);
      }
      // Rollen sätts bara på språkversioner som saknar en egen översatt roll.
      if (groupId && data.roleLabel !== undefined && data.roleLabel.trim()) {
        await supabaseAdmin
          .from("testimonials")
          .update({ role_label: data.roleLabel } as never)
          .eq("translation_group_id", groupId)
          .neq("id", data.id)
          .or("role_label.is.null,role_label.eq.")
          .is("deleted_at", null);
      }
    }

    await logTestimonialEvent({
      testimonialId: data.id,
      event: data.status ? `status_${data.status}` : "edited",
      actorEmail: actorEmail,
    });
    invalidateCached("testimonials");
    return { ok: true };
  });

/** Raderar ett omdöme permanent. */
export const deleteTestimonial = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertPageAction(context as never, "/admin/omdomen", "delete");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    await logTestimonialEvent({ event: "deleted", detail: data.id, actorEmail: actorEmail });
    const { softDeleteRow } = await import("@/lib/trash.server");
    await softDeleteRow(context as never, "testimonials", data.id);
    invalidateCached("testimonials");
    return { ok: true };
  });

/** Superadmin: sparar hur omdömen visas publikt. */
export const setTestimonialsDisplay = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ homeLimit: z.number().int().min(1).max(12), publicPage: z.boolean() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const { assertSuperadmin } = await import("@/lib/admin-guard.server");
    await assertSuperadmin(context as never);

    const { error } = await context.supabase
      .from("app_settings")
      .upsert({ key: "testimonials_display", value: data }, { onConflict: "key" });

    if (error) {
      console.error("testimonials_display write failed", error);
      throw new Error("Inställningen kunde inte sparas.");
    }
    invalidateCached("testimonials-display");
    return { ok: true as const, ...data };
  });

/** Superadmin: läser nuvarande visningsinställningar. */
export const getTestimonialsDisplayAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertSuperadmin } = await import("@/lib/admin-guard.server");
    await assertSuperadmin(context as never);
    const { parseTestimonialsDisplay } = await import("@/lib/testimonials-display");
    const { data } = await context.supabase
      .from("app_settings")
      .select("value")
      .eq("key", "testimonials_display")
      .maybeSingle();
    return parseTestimonialsDisplay(data?.value);
  });

/* ------------------------------------------------- standardtexter (superadmin) */

const LOCALES = ["sv", "en", "es"] as const;

const defaultsSchema = z.object({
  subject: z.string().trim().min(1, "Ämnesrad krävs.").max(200),
  reminderPrefix: z.string().trim().max(60).default(""),
  heading: z.string().trim().min(1, "Rubrik krävs.").max(200),
  intro: z.string().trim().max(1200).default(""),
  button: z.string().trim().max(80).default(""),
  footnote: z.string().trim().max(1200).default(""),
  path: z.string().trim().min(1, "Sökväg krävs.").max(120),
  statements: z.array(z.string().trim().max(200)).max(40).default([]),
});

export type TestimonialInviteDefaults = z.infer<typeof defaultsSchema>;

const EMPTY_DEFAULTS: TestimonialInviteDefaults = {
  subject: "",
  reminderPrefix: "",
  heading: "",
  intro: "",
  button: "",
  footnote: "",
  path: "",
  statements: [],
};

/** Superadmin: alla standardtexter för alla språk. */
export const getTestimonialInviteDefaults = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertSuperadmin } = await import("@/lib/admin-guard.server");
    await assertSuperadmin(context as never);
    const { data } = await context.supabase
      .from("app_settings")
      .select("value")
      .eq("key", "testimonial_invite_copy")
      .maybeSingle();
    const all = (data?.value ?? {}) as Record<string, Partial<TestimonialInviteDefaults>>;
    const { DEFAULT_STATEMENTS } = await import("@/lib/testimonials");
    const out = {} as Record<(typeof LOCALES)[number], TestimonialInviteDefaults>;
    for (const loc of LOCALES) {
      const saved = all[loc] ?? {};
      out[loc] = {
        ...EMPTY_DEFAULTS,
        ...saved,
        statements:
          Array.isArray(saved.statements) && saved.statements.length > 0
            ? saved.statements
            : DEFAULT_STATEMENTS[loc],
      };
    }
    return out;
  });

/** Superadmin: sparar standardtexterna för ett språk. */
export const saveTestimonialInviteDefaults = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ locale: localeSchema, copy: defaultsSchema }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const { assertSuperadmin } = await import("@/lib/admin-guard.server");
    await assertSuperadmin(context as never);
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    const { data: row } = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", "testimonial_invite_copy")
      .maybeSingle();
    const all = (row?.value ?? {}) as Record<string, Record<string, string>>;
    const next = { ...all, [data.locale]: { ...(all[data.locale] ?? {}), ...data.copy } };
    const { error } = await supabaseAdmin
      .from("app_settings")
      .upsert({ key: "testimonial_invite_copy", value: next as never }, { onConflict: "key" });
    if (error) throw new Error("Texterna kunde inte sparas.");
    await logTestimonialEvent({
      event: "invite_copy_saved",
      detail: data.locale,
      actorEmail,
      meta: { copy: data.copy },
    });
    return { ok: true as const };
  });

/** Admin: skapar (eller återanvänder) en testinbjudan för att se formuläret. */
export const createTestimonialPreviewInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ locale: localeSchema.default("sv") }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never, "/admin/omdomen");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = `preview+${data.locale}@forhandsgranskning.local`;
    const expires = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString();

    const { data: existing } = await supabaseAdmin
      .from("testimonial_invites")
      .select("id, token")
      .eq("email", email)
      .is("revoked_at", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing) {
      await supabaseAdmin
        .from("testimonial_invites")
        .update({ expires_at: expires, status: "sent", is_test: true, name: "" })
        .eq("id", existing.id);
      return { token: existing.token as string };
    }

    const token = crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().slice(0, 8);
    const { data: invite, error } = await supabaseAdmin
      .from("testimonial_invites")
      .insert({
        token,
        email,
        name: "",
        locale: data.locale,
        message: "",
        is_test: true,
        created_by_email: actorEmail,
        expires_at: expires,
      })
      .select("token")
      .single();
    if (error || !invite) throw new Error("Förhandsgranskningen kunde inte skapas.");
    return { token: invite.token as string };
  });

/* ------------------------------------------------ kontakter & autocomplete */

type ContactSuggestion = {
  email: string;
  name: string;
  locale: string;
  channel: string;
  note: string;
  lastInviteAt: string | null;
  lastInviteStatus: string;
  openedAt: string | null;
  submittedAt: string | null;
  revoked: boolean;
  inviteCount: number;
  testimonialStatus: string;
};

/** Autocomplete: adresser vi redan haft kontakt med, med historik per adress. */
export const searchTestimonialContacts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ query: z.string().trim().max(200) }).parse(data))
  .handler(async ({ data, context }): Promise<ContactSuggestion[]> => {
    await assertLightAdmin(context as never, "/admin/omdomen");
    const q = data.query.trim().toLowerCase();
    if (q.length < 2) return [];
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const like = `%${q.replace(/[%_]/g, "")}%`;
    const [{ data: invites }, { data: rows }] = await Promise.all([
      supabaseAdmin
        .from("testimonial_invites")
        .select(
          "email, name, locale, status, channel, note, created_at, opened_at, submitted_at, revoked_at",
        )
        .or(`email.ilike.${like},name.ilike.${like}`)
        .order("created_at", { ascending: false })
        .limit(60),
      supabaseAdmin
        .from("testimonials")
        .select("email, status, created_at")
        .ilike("email", like)
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(60),
    ]);

    const byEmail = new Map<string, ContactSuggestion>();
    for (const i of invites ?? []) {
      const key = String(i.email ?? "").toLowerCase();
      if (!key) continue;
      const found = byEmail.get(key);
      if (found) {
        found.inviteCount += 1;
        if (!found.name && i.name) found.name = i.name;
        continue;
      }
      byEmail.set(key, {
        email: key,
        name: i.name ?? "",
        locale: i.locale ?? "sv",
        channel: (i as { channel?: string }).channel ?? "email",
        note: (i as { note?: string }).note ?? "",
        lastInviteAt: i.created_at ?? null,
        lastInviteStatus: i.status ?? "sent",
        openedAt: i.opened_at ?? null,
        submittedAt: i.submitted_at ?? null,
        revoked: Boolean(i.revoked_at),
        inviteCount: 1,
        testimonialStatus: "",
      });
    }
    for (const t of rows ?? []) {
      const key = String(t.email ?? "").toLowerCase();
      if (!key) continue;
      const found = byEmail.get(key);
      if (found) {
        if (!found.testimonialStatus) found.testimonialStatus = t.status ?? "draft";
        continue;
      }
      byEmail.set(key, {
        email: key,
        name: "",
        locale: "sv",
        channel: "email",
        note: "",
        lastInviteAt: null,
        lastInviteStatus: "",
        openedAt: null,
        submittedAt: null,
        revoked: false,
        inviteCount: 0,
        testimonialStatus: t.status ?? "draft",
      });
    }
    return [...byEmail.values()].slice(0, 8);
  });

const manualSchema = z.object({
  email: z.string().trim().toLowerCase().email("Skriv en giltig mejladress.").max(200),
  name: z.string().trim().max(120).default(""),
  locale: localeSchema.default("sv"),
  note: z.string().trim().max(500).default(""),
});

/** Lägger in någon som kontaktats på annat sätt – inget mejl skickas. */
export const addManualTestimonialContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => manualSchema.parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never, "/admin/omdomen");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    const token = crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().slice(0, 8);
    const { data: created, error } = await supabaseAdmin
      .from("testimonial_invites")
      .insert({
        token,
        email: data.email,
        name: data.name,
        locale: data.locale,
        message: "",
        note: data.note,
        channel: "manual",
        status: "external",
        created_by_email: actorEmail,
        expires_at: new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString(),
      })
      .select("id")
      .single();
    if (error || !created) throw new Error("Kontakten kunde inte läggas till.");
    await logTestimonialEvent({
      inviteId: created.id,
      event: "manual_contact_added",
      detail: data.email,
      actorEmail,
      meta: { note: data.note, locale: data.locale },
    });
    return { ok: true as const, id: created.id, token };
  });

/** Ändrar namn, språk eller anteckning på en manuell kontakt. */
export const updateManualTestimonialContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        name: z.string().trim().max(120).default(""),
        locale: localeSchema.default("sv"),
        note: z.string().trim().max(500).default(""),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never, "/admin/omdomen");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    const { error } = await supabaseAdmin
      .from("testimonial_invites")
      .update({ name: data.name, locale: data.locale, note: data.note })
      .eq("id", data.id)
      .eq("channel", "manual");
    if (error) throw new Error("Kontakten kunde inte sparas.");
    await logTestimonialEvent({
      inviteId: data.id,
      event: "manual_contact_updated",
      actorEmail,
      meta: { note: data.note, locale: data.locale },
    });
    return { ok: true as const };
  });

/** Tar bort en manuell kontakt (bara sådana som lagts in för hand). */
export const deleteManualTestimonialContact = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never, "/admin/omdomen");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    await logTestimonialEvent({
      inviteId: data.id,
      event: "manual_contact_deleted",
      actorEmail,
    });
    const { error } = await supabaseAdmin
      .from("testimonial_invites")
      .delete()
      .eq("id", data.id)
      .eq("channel", "manual");
    if (error) throw new Error("Kontakten kunde inte tas bort.");
    return { ok: true as const };
  });

/* --------------------------------- översättning av standardtexter (superadmin) */

const TRANSLATE_LANG: Record<"sv" | "en" | "es", string> = {
  sv: "Swedish",
  en: "English",
  es: "Spanish (Spain)",
};

/** Superadmin: översätter standardtexterna till ett annat språk utan att spara. */
export const translateTestimonialInviteDefaults = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ source: localeSchema, target: localeSchema, copy: defaultsSchema }).parse(data),
  )
  .handler(async ({ data, context }): Promise<Omit<TestimonialInviteDefaults, "path">> => {
    const { assertSuperadmin } = await import("@/lib/admin-guard.server");
    await assertSuperadmin(context as never);
    const { callAiChat } = await import("@/lib/ai-gateway.server");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";

    const system = `You translate transactional email copy and short parent-facing checkbox statements for Swedish International School in Estepona, Spain, from ${TRANSLATE_LANG[data.source]} into ${TRANSLATE_LANG[data.target]}. Keep the tone warm and plain, keep proper nouns, never invent facts or add sentences. Keep the statements array in the same order and with the same number of items. Reply ONLY with valid JSON.`;
    const user = `Translate each field into ${TRANSLATE_LANG[data.target]}.

JSON in:
${JSON.stringify({
  subject: data.copy.subject,
  reminderPrefix: data.copy.reminderPrefix,
  heading: data.copy.heading,
  intro: data.copy.intro,
  button: data.copy.button,
  footnote: data.copy.footnote,
  statements: data.copy.statements,
})}

Reply with JSON: {"subject": string, "reminderPrefix": string, "heading": string, "intro": string, "button": string, "footnote": string, "statements": string[]}`;

    const { text } = await callAiChat(
      {
        feature: "testimonial.invite_copy_translate",
        surface: "admin",
        actorEmail,
        meta: { target: data.target },
      },
      {
        model: "google/gemini-2.5-flash",
        messages: [
          { role: "system", content: system },
          { role: "user", content: user },
        ],
        errorMessage: "Översättningen kunde inte göras just nu.",
      },
    );

    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(text.slice(text.indexOf("{"), text.lastIndexOf("}") + 1)) as Record<
        string,
        unknown
      >;
    } catch {
      throw new Error("Översättningen kunde inte tolkas. Försök igen.");
    }
    const str = (key: string) =>
      typeof parsed[key] === "string" ? (parsed[key] as string).trim() : "";
    const list = Array.isArray(parsed["statements"])
      ? (parsed["statements"] as unknown[])
          .filter((item): item is string => typeof item === "string")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];
    return {
      subject: str("subject"),
      reminderPrefix: str("reminderPrefix"),
      heading: str("heading"),
      intro: str("intro"),
      button: str("button"),
      footnote: str("footnote"),
      statements: list,
    };
  });

/* ------------------------------------------------ översättning av omdömen */

/** Admin: översätter ett inskickat omdöme till ett annat språk i samma grupp. */
export const translateTestimonial = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), target: localeSchema }).parse(data),
  )
  .handler(async ({ data, context }) => {
    await assertLightAdmin(context as never, "/admin/omdomen");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    const { callAiChat } = await import("@/lib/ai-gateway.server");

    const { data: source } = await supabaseAdmin
      .from("testimonials")
      .select("*")
      .eq("id", data.id)
      .is("deleted_at", null)
      .maybeSingle();
    if (!source) throw new Error("Omdömet kunde inte hittas.");
    const sourceLocale = ((source as Row)["locale"] as "sv" | "en" | "es") ?? "sv";
    if (sourceLocale === data.target) throw new Error("Omdömet är redan på det språket.");

    const text = String((source as Row)["edited_quote"] || (source as Row)["quote"] || "");
    const role = String((source as Row)["role_label"] ?? "");

    const system = `You translate short parent reviews for Swedish International School in Estepona, Spain, from ${TRANSLATE_LANG[sourceLocale]} into ${TRANSLATE_LANG[data.target]}. Keep the personal voice, keep proper nouns, never add or remove facts. Reply ONLY with valid JSON.`;
    const user = `JSON in:
${JSON.stringify({ quote: text, roleLabel: role })}

Reply with JSON: {"quote": string, "roleLabel": string}`;

    const { text: reply } = await callAiChat(
      {
        feature: "testimonial.translate",
        surface: "admin",
        actorEmail,
        meta: { target: data.target },
      },
      {
        model: "google/gemini-2.5-flash",
        messages: [
          { role: "system", content: system },
          { role: "user", content: user },
        ],
        errorMessage: "Översättningen kunde inte göras just nu.",
      },
    );
    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(reply.slice(reply.indexOf("{"), reply.lastIndexOf("}") + 1)) as Record<
        string,
        unknown
      >;
    } catch {
      throw new Error("Översättningen kunde inte tolkas. Försök igen.");
    }
    const quote = typeof parsed["quote"] === "string" ? (parsed["quote"] as string).trim() : "";
    if (!quote) throw new Error("Översättningen blev tom. Försök igen.");
    const roleLabel =
      typeof parsed["roleLabel"] === "string" ? (parsed["roleLabel"] as string).trim() : role;

    const src = source as Row;
    const groupId = String(src["translation_group_id"] ?? src["id"]);
    const { data: existing } = await supabaseAdmin
      .from("testimonials")
      .select("id")
      .eq("translation_group_id", groupId)
      .eq("locale", data.target)
      .is("deleted_at", null)
      .maybeSingle();

    let id = existing?.id ?? "";
    if (existing) {
      await supabaseAdmin
        .from("testimonials")
        .update({ quote, edited_quote: quote, role_label: roleLabel } as never)
        .eq("id", existing.id);
    } else {
      const { data: created, error } = await supabaseAdmin
        .from("testimonials")
        .insert({
          invite_id: src["invite_id"] as string | null,
          email: String(src["email"] ?? ""),
          locale: data.target,
          rating: Number(src["rating"] ?? 5),
          quote,
          edited_quote: quote,
          quote_source: String(src["quote_source"] ?? "own"),
          first_name: String(src["first_name"] ?? ""),
          last_name: String(src["last_name"] ?? ""),
          role_label: roleLabel,
          original_image_url: String(src["original_image_url"] ?? ""),
          image_url: String(src["image_url"] ?? ""),
          image_choice: String(src["image_choice"] ?? "none"),
          image_background: String(src["image_background"] ?? ""),
          image_variants: (src["image_variants"] ?? []) as never,
          consent_given: Boolean(src["consent_given"]),
          consent_at: (src["consent_at"] as string | null) ?? null,
          consent_text: String(src["consent_text"] ?? ""),
          social_consent: Boolean(src["social_consent"]),
          social_consent_text: String(src["social_consent_text"] ?? ""),
          status: String(src["status"] ?? "submitted"),
          is_test: Boolean(src["is_test"]),
          sort_order: Number(src["sort_order"] ?? 0),
          show_on_home: src["show_on_home"] !== false,
          translation_group_id: groupId,
          published_at: (src["published_at"] as string | null) ?? null,
        } as never)
        .select("id")
        .single();
      if (error || !created) throw new Error("Översättningen kunde inte sparas.");
      id = created.id as string;
    }

    await logTestimonialEvent({
      testimonialId: id,
      event: "translated",
      detail: `${sourceLocale} → ${data.target}`,
      actorEmail,
    });
    invalidateCached("testimonials");
    return { ok: true as const, id, locale: data.target };
  });

/* --------------------------------- omdöme inlagt för hand (t.ex. via mejl) */

const manualTestimonialSchema = z.object({
  email: z.string().trim().toLowerCase().max(200).default(""),
  firstName: z.string().trim().max(60).default(""),
  lastName: z.string().trim().max(60).default(""),
  roleLabel: z.string().trim().max(80).default(""),
  locale: localeSchema.default("sv"),
  rating: z.number().int().min(1).max(5).default(5),
  quote: z.string().trim().min(10, "Skriv omdömestexten.").max(1000, "Omdömet får vara högst 1000 tecken."),
  improvementFeedback: z.string().trim().max(1000).default(""),
  source: z.string().trim().max(120).default(""),
  adminNote: z.string().trim().max(600).default(""),
  showFirstName: z.boolean().default(true),
  showLastName: z.boolean().default(true),
  showRole: z.boolean().default(true),
  consentGiven: z.boolean().default(false),
  socialConsent: z.boolean().default(false),
  publish: z.boolean().default(false),
});

/** Lägger in ett omdöme som kommit in på annat sätt, t.ex. via mejl. */
export const addManualTestimonial = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => manualTestimonialSchema.parse(data))
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never, "/admin/omdomen");
    const actorEmail = (context.claims as { email?: string } | undefined)?.email ?? "";
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { logTestimonialEvent } = await import("@/lib/testimonials.server");
    const now = new Date().toISOString();
    if (data.publish && !data.consentGiven) {
      throw new Error("Omdömet kan bara publiceras när familjen har godkänt det.");
    }
    const { data: created, error } = await supabaseAdmin
      .from("testimonials")
      .insert({
        email: data.email,
        locale: data.locale,
        rating: data.rating,
        quote: data.quote,
        edited_quote: data.quote,
        quote_source: "manual",
        first_name: data.firstName,
        last_name: data.lastName,
        role_label: data.roleLabel,
        improvement_feedback: data.improvementFeedback,
        display_prefs: {
          firstName: data.showFirstName,
          lastName: data.showLastName,
          role: data.showRole,
        },
        source_channel: data.source,
        admin_note: data.adminNote.slice(0, 600),
        consent_given: data.consentGiven,
        consent_at: data.consentGiven ? now : null,
        consent_text: data.consentGiven ? CONSENT_TEXT[data.locale] : "",
        social_consent: data.socialConsent,
        social_consent_text: data.socialConsent ? SOCIAL_CONSENT_TEXT[data.locale] : "",
        status: data.publish ? "published" : "submitted",
        published_at: data.publish ? now : null,
        reviewed_by_email: actorEmail,
      } as never)
      .select("id")
      .single();
    if (error || !created) throw new Error("Omdömet kunde inte läggas till.");
    await logTestimonialEvent({
      testimonialId: created.id,
      event: "manual_testimonial_added",
      detail: data.source || data.email,
      actorEmail,
      meta: { locale: data.locale, rating: data.rating, published: data.publish },
    });
    invalidateCached("testimonials");
    return { ok: true as const, id: created.id };
  });
